import { MapPin, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type LocalAddress, useCreateAccountFlow } from '@/context/create-account-flow';
import { useToast } from '@/hooks/use-toast';
import { AddressCard } from './address-card';
import { AddressFormDialog } from './address-form-dialog';

export function StepAddresses() {
  const navigate = useNavigate();
  const [_searchParams] = useSearchParams();
  const { toast } = useToast();

  const {
    account,
    addresses,
    createAddressLocally,
    updateAddressLocally,
    deleteAddressLocally,
    resetFlow,
  } = useCreateAccountFlow();

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<LocalAddress | undefined>();
  const [duplicatingAddress, setDuplicatingAddress] = useState<LocalAddress | undefined>();

  // Check if account exists
  if (!account?.document?.value) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conta não encontrada</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Os dados da conta não foram encontrados. Isso pode acontecer se você navegou diretamente
            para esta página ou se os dados foram perdidos.
          </p>
          <Button onClick={() => navigate('/commercial/accounts/create?step=account')}>
            Criar nova conta
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleCreateAddress = (data: LocalAddress) => {
    createAddressLocally(data);
    toast({
      variant: 'success',
      title: 'Sucesso!',
      description: 'Endereço adicionado com sucesso!',
    });
  };

  const handleDuplicateAddress = (data: LocalAddress) => {
    createAddressLocally(data);
    setDuplicatingAddress(undefined);
    toast({
      variant: 'success',
      title: 'Sucesso!',
      description: 'Endereço duplicado com sucesso!',
    });
  };

  const handleUpdateAddress = (data: LocalAddress) => {
    if (selectedAddress) {
      updateAddressLocally(selectedAddress.id, data);
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Endereço atualizado com sucesso!',
      });
    }
  };

  const handleDeleteAddress = () => {
    if (selectedAddress) {
      deleteAddressLocally(selectedAddress.id);
      setDeleteDialogOpen(false);
      setSelectedAddress(undefined);
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Endereço excluído com sucesso!',
      });
    }
  };

  const handleCancel = () => {
    resetFlow();
    toast({
      variant: 'info',
      title: 'Cancelado!',
      description: 'O fluxo de criação foi cancelado.',
    });
    navigate('/commercial/accounts');
  };

  const handleNext = () => {
    navigate('/commercial/accounts/create?step=contacts');
  };

  return (
    <div className="space-y-6">
      <div className="w-full max-w-[1400px] mx-auto space-y-6">
        <div className="grid grid-cols-1 gap-6 items-stretch">
          <Card className="w-full">
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="h-5 w-5 text-primary" />
                  Endereços
                  <Badge variant="secondary" className="text-xs">
                    {addresses.length}
                  </Badge>
                </CardTitle>
                <Button variant="outline" onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <div className="lg:col-span-2 h-full space-y-3">
                {addresses.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {addresses.map((addr) => {
                      const isFiscalApi = addr.type?.includes('Fiscal') ?? false;

                      return (
                        <AddressCard
                          key={addr.id}
                          title="Endereço:"
                          address={addr}
                          onEdit={
                            isFiscalApi
                              ? undefined
                              : () => {
                                  setSelectedAddress(addr);
                                  setEditDialogOpen(true);
                                }
                          }
                          onDelete={
                            isFiscalApi
                              ? undefined
                              : () => {
                                  setSelectedAddress(addr);
                                  setDeleteDialogOpen(true);
                                }
                          }
                          onDuplicate={
                            isFiscalApi
                              ? (address) => {
                                  setDuplicatingAddress(address);
                                  setDuplicateDialogOpen(true);
                                }
                              : undefined
                          }
                        />
                      );
                    })}
                  </div>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle>Nenhum endereço encontrado</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p>Cadastre um endereço para este cliente.</p>
                      <Button onClick={() => setCreateDialogOpen(true)} variant="outline">
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar Endereço
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex gap-4 items-end justify-end mt-4">
        <Button onClick={handleCancel} type="button" variant="ghost">
          Cancelar
        </Button>
        {account && addresses.length > 0 && (
          <Button onClick={handleNext} className="bg-black text-white hover:bg-gray-800">
            Salvar e próximo
          </Button>
        )}
      </div>

      {/* Create Address Dialog */}
      <AddressFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateAddress}
        mode="create"
      />

      {/* Duplicate Address Dialog */}
      <AddressFormDialog
        open={duplicateDialogOpen}
        onClose={() => {
          setDuplicateDialogOpen(false);
          setDuplicatingAddress(undefined);
        }}
        onSubmit={handleDuplicateAddress}
        address={duplicatingAddress}
        defaultValues={
          duplicatingAddress
            ? {
                zip_code: duplicatingAddress.zip_code || '',
                street: duplicatingAddress.street || '',
                number: duplicatingAddress.number || '',
                complement: duplicatingAddress.complement || '',
                district: duplicatingAddress.district || '',
                city: duplicatingAddress.city || '',
                state: duplicatingAddress.state || '',
                reference_point: duplicatingAddress.reference_point || '',
                type: [],
              }
            : undefined
        }
        mode="create"
        duplicateMode
      />

      {/* Edit Address Dialog */}
      <AddressFormDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedAddress(undefined);
        }}
        onSubmit={handleUpdateAddress}
        address={selectedAddress}
        mode="edit"
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Endereço</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este endereço? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedAddress(undefined)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAddress}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
